import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  AutoAwesome,
  Bolt,
  CampaignOutlined,
  CheckCircle,
  Close,
  Lock,
  Refresh,
  Schedule,
  Send,
} from '@mui/icons-material';
import { apiService } from '../../services/apiService';
import WhatsAppPhonePreview from './WhatsAppPhonePreview';
import AudienceReviewDialog, { ReviewMember } from '../audience/AudienceReviewDialog';
import { TEMPLATE_STATUS, prettyKey, renderTemplate, wa } from './waTheme';

export interface WorkflowMeta {
  segments: Array<{
    key: string;
    label: string;
    description: string;
    icon: string;
    fields: Array<{ key: string; label: string; type: 'number' | 'userIds'; default?: number; min?: number; max?: number; suffix?: string }>;
    provides: string[];
    dedupe: 'member' | 'entity';
    automatable: boolean;
  }>;
  templates: Array<{
    templateKey: string;
    status: string;
    bodyPreview?: string;
    variableOrder: string[];
    providerTemplateName?: string;
    rejectionReason?: string;
  }>;
  autoVariables: Record<string, string>;
}

type Kind = 'campaign' | 'automation';

const STEPS = [
  { key: 'type', label: 'Type', icon: '⚡' },
  { key: 'audience', label: 'Audience', icon: '👥' },
  { key: 'message', label: 'Message', icon: '💬' },
  { key: 'launch', label: 'Launch', icon: '🚀' },
];

/** "YYYY-MM-DDTHH:mm" for now + 1h, in IST. */
function nextHourIstLocal(): string {
  const d = new Date(Date.now() + 330 * 60000 + 3600000);
  d.setUTCMinutes(0, 0, 0);
  return d.toISOString().slice(0, 16);
}
function nowIstLocal(): string {
  return new Date(Date.now() + 330 * 60000).toISOString().slice(0, 16);
}

const SectionTitle: React.FC<{ children: React.ReactNode; hint?: string }> = ({ children, hint }) => (
  <Box sx={{ mb: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Box sx={{ width: 4, height: 20, bgcolor: wa.teal, borderRadius: 2 }} />
      <Typography variant="subtitle1" fontWeight={800}>{children}</Typography>
    </Box>
    {hint && (
      <Typography variant="body2" color="text.secondary" sx={{ ml: 1.6, mt: 0.3 }}>{hint}</Typography>
    )}
  </Box>
);

const WorkflowBuilder: React.FC<{
  open: boolean;
  onClose: () => void;
  onCreated: (msg: string) => void;
  studioId: string;
  senderName: string;
  isPlatform: boolean;
  meta: WorkflowMeta | null;
  initialKind?: Kind;
}> = ({ open, onClose, onCreated, studioId, senderName, isPlatform, meta, initialKind }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));

  const [step, setStep] = useState(0);
  const [kind, setKind] = useState<Kind>(initialKind ?? 'campaign');
  const [segment, setSegment] = useState('');
  const [segmentConfig, setSegmentConfig] = useState<Record<string, any>>({});
  const [members, setMembers] = useState<any[]>([]);
  const [excluded, setExcluded] = useState<string[]>([]);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewMembers, setReviewMembers] = useState<ReviewMember[] | null>(null);
  const [memberQuery, setMemberQuery] = useState('');
  const [memberOptions, setMemberOptions] = useState<any[]>([]);
  const [templateKey, setTemplateKey] = useState('');
  const [params, setParams] = useState<Record<string, string>>({});
  const [name, setName] = useState('');
  const [sendMode, setSendMode] = useState<'now' | 'schedule'>('now');
  const [scheduledLocal, setScheduledLocal] = useState(nextHourIstLocal());
  const [timeIST, setTimeIST] = useState('10:00');
  const [cooldownDays, setCooldownDays] = useState(30);
  const [preview, setPreview] = useState<any>(null);
  const [previewing, setPreviewing] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Fresh builder every time it opens.
  useEffect(() => {
    if (!open) return;
    setStep(initialKind ? 1 : 0);
    setKind(initialKind ?? 'campaign');
    setSegment('');
    setSegmentConfig({});
    setMembers([]);
    setExcluded([]);
    setTemplateKey('');
    setParams({});
    setName('');
    setSendMode('now');
    setScheduledLocal(nextHourIstLocal());
    setTimeIST('10:00');
    setCooldownDays(30);
    setPreview(null);
    setError(null);
  }, [open, initialKind]);

  const segDef = meta?.segments.find((s) => s.key === segment);
  const tpl = meta?.templates.find((t) => t.templateKey === templateKey);
  const provides = useMemo(() => new Set(segDef?.provides ?? []), [segDef]);
  const effectiveConfig = useMemo(
    () =>
      segment === 'SPECIFIC_MEMBERS'
        ? { userIds: members.map((m) => m._id) }
        : { ...segmentConfig, ...(excluded.length ? { excludeUserIds: excluded } : {}) },
    [segment, segmentConfig, members, excluded],
  );

  // "Browse all members" for Specific members: tick from the full list.
  const [browseOpen, setBrowseOpen] = useState(false);
  const [browseAll, setBrowseAll] = useState<any[] | null>(null);
  const openBrowse = async () => {
    setBrowseOpen(true);
    setBrowseAll(null);
    try {
      const res: any = await apiService.previewWorkflow(
        { segment: 'ALL_MEMBERS', segmentConfig: {}, includeMembers: true },
        studioId,
      );
      setBrowseAll((res?.data ?? res)?.members ?? []);
    } catch (e: any) {
      setBrowseOpen(false);
      setError(e?.message ?? 'Could not load members');
    }
  };

  // "Review recipients": everyone matched, with the details this audience knows.
  const openReview = async () => {
    setReviewOpen(true);
    setReviewMembers(null);
    try {
      const res: any = await apiService.previewWorkflow(
        { segment, segmentConfig: effectiveConfig, includeMembers: true },
        studioId,
      );
      const data = res?.data ?? res;
      setReviewMembers(
        (data?.members ?? []).map((m: any) => {
          const v = m.vars ?? {};
          const detail = v.className
            ? `${v.className} · ${v.startsAt ?? ''}`
            : v.packName
              ? `${v.packName}${v.remaining ? ` · ${v.remaining} left` : ''}${v.expiryDate ? ` · exp ${v.expiryDate}` : ''}`
              : isPlatform
                ? undefined
                : v.studioName;
          return { id: m.id, name: m.name, subtitle: m.phone, detail };
        }),
      );
    } catch (e: any) {
      setReviewOpen(false);
      setError(e?.message ?? 'Could not load recipients');
    }
  };

  // Live audience preview (debounced). Audience only — the message itself is
  // rendered locally, so typing a variable never re-queries the database.
  const previewSeq = useRef(0);
  const runPreview = useCallback(async () => {
    const seq = ++previewSeq.current;
    if (!segment || (segment === 'SPECIFIC_MEMBERS' && !members.length)) {
      setPreview(null);
      return;
    }
    setPreviewing(true);
    try {
      const res: any = await apiService.previewWorkflow({ segment, segmentConfig: effectiveConfig }, studioId);
      if (seq === previewSeq.current) setPreview(res?.data ?? res);
    } catch (e: any) {
      if (seq === previewSeq.current) setPreview({ error: e?.message ?? 'Could not load the audience' });
    } finally {
      if (seq === previewSeq.current) setPreviewing(false);
    }
  }, [segment, effectiveConfig, studioId, members.length]);

  useEffect(() => {
    if (!open || step < 1) return;
    const t = setTimeout(runPreview, 450);
    return () => clearTimeout(t);
  }, [open, step, runPreview]);

  // Member search for "Specific members".
  useEffect(() => {
    if (segment !== 'SPECIFIC_MEMBERS') return;
    const t = setTimeout(async () => {
      try {
        const res: any = await apiService.searchMessagingMembers(memberQuery, studioId);
        setMemberOptions(((res?.data ?? res)?.members ?? []).filter((m: any) => m.phoneNumber));
      } catch {
        setMemberOptions([]);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [memberQuery, segment, studioId]);

  const missingVars = (tpl?.variableOrder ?? []).filter(
    (v) => !provides.has(v) && !String(params[v] ?? '').trim(),
  );

  const timeOk = /^(0[7-9]|1\d|20):[0-5]\d$|^21:00$/.test(timeIST);
  const recipients = preview?.uniqueMembers ?? preview?.count ?? 0;

  const canNext =
    step === 0 ||
    (step === 1 && !!segment && (segment !== 'SPECIFIC_MEMBERS' || members.length > 0)) ||
    (step === 2 && !!tpl && tpl.status === 'approved' && missingVars.length === 0);

  const sampleVars = preview?.sample?.[0]?.vars ?? {};
  const bubble = tpl
    ? renderTemplate(tpl.bodyPreview, tpl.variableOrder, { ...sampleVars, ...params }, (n) =>
        provides.has(n) ? `‹${meta?.autoVariables?.[n] ?? n}›` : `[${n}]`,
      )
    : undefined;

  const scheduleInvalid = kind === 'campaign' && sendMode === 'schedule' && scheduledLocal <= nowIstLocal();

  const submit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const payload: any = {
        name: name.trim() || `${segDef?.label ?? 'Audience'} · ${prettyKey(templateKey)}`,
        kind,
        templateKey,
        segment,
        segmentConfig: effectiveConfig,
        params,
      };
      if (kind === 'campaign' && sendMode === 'schedule') payload.scheduledAt = `${scheduledLocal}:00+05:30`;
      if (kind === 'automation') {
        payload.automation = { timeIST, ...(segDef?.dedupe === 'member' ? { cooldownDays } : {}) };
      }
      await apiService.createWorkflow(payload, studioId);
      onCreated(
        kind === 'automation'
          ? `Automation started — runs every day at ${timeIST} IST`
          : sendMode === 'schedule'
            ? 'Campaign scheduled'
            : `Sending to ${recipients} members…`,
      );
    } catch (e: any) {
      setError(e?.message ?? 'Could not create the workflow');
    } finally {
      setSubmitting(false);
    }
  };

  /* ──────────────── step bodies ──────────────── */

  const typeStep = (
    <Box>
      <SectionTitle hint="Pick how this workflow sends.">What do you want to set up?</SectionTitle>
      <Grid container spacing={2}>
        {([
          {
            k: 'campaign' as Kind,
            icon: <CampaignOutlined sx={{ fontSize: 34 }} />,
            title: 'Campaign',
            body: 'Send one WhatsApp message to an audience — right now or at a time you pick.',
            eg: 'e.g. “Studio closed on Diwali”, a payment reminder to specific members',
          },
          {
            k: 'automation' as Kind,
            icon: <Bolt sx={{ fontSize: 34 }} />,
            title: 'Automation',
            body: 'A rule that runs every day by itself and messages whoever matches — never the same thing twice.',
            eg: 'e.g. “Pack expires in 3 days”, “Class tomorrow” reminders, win back inactive members',
          },
        ]).map((o) => {
          const active = kind === o.k;
          return (
            <Grid key={o.k} size={{ xs: 12, sm: 6 }}>
              <Box
                onClick={() => {
                  setKind(o.k);
                  const current = meta?.segments.find((x) => x.key === segment);
                  if (o.k === 'automation' && current && !current.automatable) {
                    setSegment('');
                    setExcluded([]);
                  }
                }}
                sx={{
                  cursor: 'pointer', p: 2.5, borderRadius: 3, height: '100%',
                  border: `2px solid ${active ? wa.teal : 'rgba(0,0,0,0.08)'}`,
                  bgcolor: active ? wa.tint : '#fff',
                  transition: 'all .15s',
                  '&:hover': { borderColor: wa.teal, boxShadow: '0 6px 24px rgba(18,140,126,0.12)' },
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1.5} mb={1}>
                  <Box sx={{ width: 52, height: 52, borderRadius: 2.5, display: 'grid', placeItems: 'center', bgcolor: active ? wa.teal : 'rgba(18,140,126,0.1)', color: active ? '#fff' : wa.teal }}>
                    {o.icon}
                  </Box>
                  <Typography variant="h6" fontWeight={800} flexGrow={1}>{o.title}</Typography>
                  {active && <CheckCircle sx={{ color: wa.teal }} />}
                </Stack>
                <Typography variant="body2" sx={{ mb: 1 }}>{o.body}</Typography>
                <Typography variant="caption" color="text.secondary">{o.eg}</Typography>
              </Box>
            </Grid>
          );
        })}
      </Grid>
    </Box>
  );

  const audienceStep = (
    <Box>
      <SectionTitle hint={isPlatform ? 'Only SweatSocial app users can ever receive messages from this number.' : 'Only your studio’s members with a phone number are included.'}>
        Who should get it?
      </SectionTitle>
      <Grid container spacing={1.5}>
        {(meta?.segments ?? []).map((s) => {
          const disabled = kind === 'automation' && !s.automatable;
          const active = segment === s.key;
          return (
            <Grid key={s.key} size={{ xs: 12, sm: 6, lg: 4 }}>
              <Tooltip title={disabled ? 'One-off audience — send it as a campaign.' : ''}>
                <Box
                  onClick={() => {
                    if (disabled) return;
                    setSegment(s.key);
                    setExcluded([]);
                    setSegmentConfig(Object.fromEntries(s.fields.filter((f) => f.type === 'number').map((f) => [f.key, f.default])));
                  }}
                  sx={{
                    p: 1.6, borderRadius: 2.5, height: '100%', cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.45 : 1,
                    border: `1.5px solid ${active ? wa.teal : 'rgba(0,0,0,0.08)'}`,
                    bgcolor: active ? wa.tint : '#fff',
                    transition: 'all .15s',
                    '&:hover': disabled ? {} : { borderColor: wa.teal },
                  }}
                >
                  <Stack direction="row" spacing={1.2} alignItems="flex-start">
                    <Typography sx={{ fontSize: 22, lineHeight: 1.2 }}>{s.icon}</Typography>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontWeight={700} fontSize={14}>
                        {isPlatform && s.key === 'ALL_MEMBERS' ? 'All SweatSocial app users' : s.label}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                    </Box>
                  </Stack>
                </Box>
              </Tooltip>
            </Grid>
          );
        })}
      </Grid>

      {segDef && (segDef.fields.length > 0) && (
        <Box sx={{ mt: 2, p: 2, borderRadius: 2.5, bgcolor: 'rgba(0,0,0,0.02)', border: '1px dashed rgba(0,0,0,0.12)' }}>
          {segDef.fields.map((f) =>
            f.type === 'number' ? (
              <TextField
                key={f.key}
                type="number"
                size="small"
                label={f.label}
                value={segmentConfig[f.key] ?? f.default ?? ''}
                onChange={(e) =>
                  setSegmentConfig({ ...segmentConfig, [f.key]: e.target.value === '' ? undefined : Number(e.target.value) })
                }
                inputProps={{ min: f.min, max: f.max }}
                InputProps={{ endAdornment: f.suffix ? <InputAdornment position="end">{f.suffix}</InputAdornment> : undefined }}
                sx={{ width: 240 }}
              />
            ) : (
              <Box key={f.key}>
              <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                  {members.length ? `${members.length} selected` : 'Search, or pick from everyone:'}
                </Typography>
                <Button size="small" variant="outlined" onClick={openBrowse} sx={{ borderColor: wa.teal, color: wa.teal, fontWeight: 700, borderRadius: 2 }}>
                  Browse all members
                </Button>
              </Stack>
              <Autocomplete
                multiple
                options={memberOptions}
                value={members}
                filterOptions={(x) => x}
                getOptionLabel={(m: any) => `${m.name || 'Unnamed'} · ${m.phoneNumber ?? ''}`}
                isOptionEqualToValue={(a: any, b: any) => a._id === b._id}
                onChange={(_, v) => setMembers(v)}
                onInputChange={(_, v) => setMemberQuery(v)}
                renderInput={(p) => <TextField {...p} size="small" label="Search members by name, phone or email" />}
                renderTags={(value, getTagProps) =>
                  value.map((m: any, i) => <Chip {...getTagProps({ index: i })} key={m._id} size="small" label={m.name || m.phoneNumber} />)
                }
              />
              </Box>
            ),
          )}
        </Box>
      )}

      {segDef && (
        <Box sx={{ mt: 2, p: 2, borderRadius: 2.5, bgcolor: '#fff', border: `1px solid ${wa.tintBorder}` }}>
          <Stack direction="row" alignItems="center" spacing={1} mb={preview?.sample?.length ? 1.2 : 0}>
            {previewing ? <CircularProgress size={18} sx={{ color: wa.teal }} /> : <Refresh sx={{ color: wa.teal, cursor: 'pointer' }} onClick={runPreview} />}
            {preview?.error ? (
              <Typography color="error" variant="body2">{preview.error}</Typography>
            ) : (
              <Typography fontWeight={800} sx={{ flexGrow: 1 }}>
                {preview ? `${recipients} ${recipients === 1 ? 'member' : 'members'} ${kind === 'automation' ? 'match today' : 'will receive this'}` : 'Counting…'}
                {preview?.truncated && ' (one send is capped at 10,000)'}
              </Typography>
            )}
            {excluded.length > 0 && (
              <Chip size="small" color="warning" label={`${excluded.length} left out`} onDelete={() => setExcluded([])} />
            )}
            {segment !== 'SPECIFIC_MEMBERS' && (preview?.matched ?? preview?.count ?? 0) > 0 && (
              <Button size="small" onClick={openReview} sx={{ fontWeight: 800, color: wa.teal }}>
                Review recipients
              </Button>
            )}
          </Stack>
          {recipients > 250 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              WhatsApp limits how many different people a number can message per day (new numbers: about 250–1,000).
              Anyone over your number’s limit shows as failed in the delivery log — you can resend to them the next day.
            </Typography>
          )}
          {kind === 'automation' && excluded.length > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              People you leave out are skipped on every future run too.
            </Typography>
          )}
          {!!preview?.sample?.length && (
            <Stack direction="row" flexWrap="wrap" gap={0.8}>
              {preview.sample.map((m: any, i: number) => (
                <Chip key={i} size="small" label={`${m.name} · ${m.phone}`} sx={{ bgcolor: wa.tint }} />
              ))}
              {preview.count > preview.sample.length && (
                <Chip size="small" variant="outlined" label={`+${preview.count - preview.sample.length} more`} />
              )}
            </Stack>
          )}
          {preview && preview.count === 0 && !preview.error && (
            <Typography variant="caption" color="text.secondary">
              {kind === 'automation' ? 'Nobody matches right now — the automation will pick members up as they match.' : 'Nobody matches this audience right now.'}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );

  const messageStep = (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 7 }}>
        <SectionTitle hint="WhatsApp only delivers templates it has approved. Variables marked Auto are filled for each member.">
          Choose the message
        </SectionTitle>
        {!meta?.templates.length && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            No templates are linked yet. Open <b>Setup → Templates</b> to link your approved WhatsApp templates.
          </Alert>
        )}
        <Stack spacing={1.2}>
          {(meta?.templates ?? []).map((t) => {
            const ok = t.status === 'approved';
            const active = templateKey === t.templateKey;
            const st = TEMPLATE_STATUS[t.status] ?? TEMPLATE_STATUS.draft;
            return (
              <Box
                key={t.templateKey}
                onClick={() => {
                  if (!ok) return;
                  setTemplateKey(t.templateKey);
                  setParams({});
                }}
                sx={{
                  p: 1.6, borderRadius: 2.5, cursor: ok ? 'pointer' : 'not-allowed', opacity: ok ? 1 : 0.55,
                  border: `1.5px solid ${active ? wa.teal : 'rgba(0,0,0,0.08)'}`, bgcolor: active ? wa.tint : '#fff',
                  transition: 'all .15s', '&:hover': ok ? { borderColor: wa.teal } : {},
                }}
              >
                <Stack direction="row" alignItems="center" spacing={1} mb={0.6}>
                  <Typography fontWeight={800} fontSize={14} flexGrow={1}>{prettyKey(t.templateKey)}</Typography>
                  {!ok && <Lock sx={{ fontSize: 15, color: 'text.disabled' }} />}
                  <Chip size="small" label={st.label} sx={{ height: 20, fontSize: 11, fontWeight: 700, color: st.color, bgcolor: st.bg }} />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                  {t.bodyPreview || '—'}
                </Typography>
              </Box>
            );
          })}
        </Stack>

        {tpl && tpl.variableOrder.length > 0 && (
          <Box sx={{ mt: 2.5 }}>
            <Typography fontWeight={800} mb={1}>Fill in the variables</Typography>
            <Stack spacing={1.2}>
              {tpl.variableOrder.map((v, i) => {
                const auto = provides.has(v);
                return (
                  <Box key={v} sx={{ display: 'flex', gap: 1.2, alignItems: 'center', flexWrap: 'wrap' }}>
                    <Chip label={`{{${i + 1}}}`} size="small" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                    <TextField
                      size="small"
                      sx={{ flex: 1, minWidth: 220 }}
                      label={meta?.autoVariables?.[v] ?? v}
                      placeholder={auto ? 'Leave blank to fill automatically' : 'Required'}
                      value={params[v] ?? ''}
                      onChange={(e) => setParams({ ...params, [v]: e.target.value })}
                      error={!auto && !String(params[v] ?? '').trim()}
                      InputProps={{
                        endAdornment: auto && !String(params[v] ?? '').trim() ? (
                          <InputAdornment position="end">
                            <Chip icon={<AutoAwesome sx={{ fontSize: 14 }} />} label="Auto" size="small" sx={{ height: 22, bgcolor: wa.tint, color: wa.dark, fontWeight: 700 }} />
                          </InputAdornment>
                        ) : undefined,
                      }}
                    />
                  </Box>
                );
              })}
            </Stack>
            {missingVars.length > 0 && (
              <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
                This audience can’t fill {missingVars.map((v) => `“${meta?.autoVariables?.[v] ?? v}”`).join(', ')} — type a value.
              </Typography>
            )}
          </Box>
        )}
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <Box sx={{ position: { md: 'sticky' }, top: 8 }}>
          <WhatsAppPhonePreview
            senderName={senderName}
            message={bubble}
            caption={preview?.sample?.[0] ? `Preview for ${preview.sample[0].name}` : 'Sample preview'}
          />
        </Box>
      </Grid>
    </Grid>
  );

  const launchStep = (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 7 }}>
        <SectionTitle>{kind === 'automation' ? 'When should it run?' : 'When should it go out?'}</SectionTitle>

        {kind === 'campaign' ? (
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} mb={2}>
            {(['now', 'schedule'] as const).map((m) => (
              <Box
                key={m}
                onClick={() => setSendMode(m)}
                sx={{
                  flex: 1, p: 1.8, borderRadius: 2.5, cursor: 'pointer',
                  border: `1.5px solid ${sendMode === m ? wa.teal : 'rgba(0,0,0,0.08)'}`, bgcolor: sendMode === m ? wa.tint : '#fff',
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center">
                  {m === 'now' ? <Send sx={{ color: wa.teal }} /> : <Schedule sx={{ color: '#e65100' }} />}
                  <Typography fontWeight={800}>{m === 'now' ? 'Send now' : 'Schedule'}</Typography>
                </Stack>
                <Typography variant="caption" color="text.secondary">
                  {m === 'now' ? 'Goes out within a minute.' : 'Pick a date and time (IST).'}
                </Typography>
              </Box>
            ))}
          </Stack>
        ) : (
          <Box sx={{ p: 2, borderRadius: 2.5, bgcolor: wa.tint, border: `1px solid ${wa.tintBorder}`, mb: 2 }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ sm: 'center' }}>
              <TextField
                type="time"
                label="Every day at (IST)"
                size="small"
                value={timeIST}
                onChange={(e) => setTimeIST(e.target.value)}
                InputLabelProps={{ shrink: true }}
                error={!timeOk}
                helperText={timeOk ? '07:00 – 21:00 IST' : 'Between 07:00 and 21:00 — no messages at night'}
                sx={{ width: 220 }}
              />
              {segDef?.dedupe === 'member' && (
                <TextField
                  type="number"
                  label="Message each member at most once every"
                  size="small"
                  value={cooldownDays}
                  onChange={(e) => setCooldownDays(Math.max(1, Number(e.target.value)))}
                  InputProps={{ endAdornment: <InputAdornment position="end">days</InputAdornment> }}
                  sx={{ width: 320 }}
                />
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              {segDef?.dedupe === 'entity'
                ? 'Each pack or booking is messaged once — members never get the same reminder twice.'
                : 'A member who keeps matching is messaged again only after the cooldown.'}
            </Typography>
          </Box>
        )}

        {kind === 'campaign' && sendMode === 'schedule' && (
          <TextField
            type="datetime-local"
            label="Send at (IST)"
            size="small"
            value={scheduledLocal}
            onChange={(e) => setScheduledLocal(e.target.value)}
            inputProps={{ min: nowIstLocal() }}
            InputLabelProps={{ shrink: true }}
            error={scheduleInvalid}
            helperText={scheduleInvalid ? 'Pick a time in the future' : ' '}
            sx={{ mb: 1, width: 260 }}
          />
        )}

        <TextField
          fullWidth
          size="small"
          label="Workflow name (internal)"
          placeholder={`${segDef?.label ?? 'Audience'} · ${prettyKey(templateKey || 'message')}`}
          value={name}
          onChange={(e) => setName(e.target.value)}
          sx={{ mb: 2 }}
        />

        <Box sx={{ p: 2, borderRadius: 2.5, border: '1px solid rgba(0,0,0,0.07)', bgcolor: '#fff' }}>
          {[
            ['Type', kind === 'automation' ? 'Automation · runs daily' : 'Campaign · one send'],
            ['From', senderName],
            ['Audience', `${segDef?.icon ?? ''} ${segDef?.label ?? ''}${preview ? ` · ${recipients} ${kind === 'automation' ? 'match today' : 'members'}` : ''}${excluded.length ? ` · ${excluded.length} left out` : ''}`],
            ['Template', prettyKey(templateKey)],
            ['When', kind === 'automation' ? `Every day at ${timeIST} IST` : sendMode === 'now' ? 'Right now' : `${scheduledLocal.replace('T', ' ')} IST`],
          ].map(([k, v]) => (
            <Stack key={k} direction="row" sx={{ py: 0.7, borderBottom: '1px dashed rgba(0,0,0,0.06)', '&:last-child': { border: 0 } }}>
              <Typography variant="body2" color="text.secondary" sx={{ width: 90, flexShrink: 0 }}>{k}</Typography>
              <Typography variant="body2" fontWeight={600}>{v}</Typography>
            </Stack>
          ))}
        </Box>
        {error && <Alert severity="error" sx={{ mt: 2 }}>{error}</Alert>}
      </Grid>
      <Grid size={{ xs: 12, md: 5 }}>
        <WhatsAppPhonePreview senderName={senderName} message={bubble} caption="What members will see" />
      </Grid>
    </Grid>
  );

  const body = [typeStep, audienceStep, messageStep, launchStep][step];

  return (
    <Dialog open={open} onClose={onClose} fullScreen={fullScreen} maxWidth="lg" fullWidth PaperProps={{ sx: { borderRadius: fullScreen ? 0 : 4 } }}>
      {/* Header */}
      <Box sx={{ background: wa.gradient, color: '#fff', px: 3, py: 2.2, position: 'relative' }}>
        <Stack direction="row" alignItems="center" spacing={1.5}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h6" fontWeight={800} sx={{ color: '#fff' }}>New WhatsApp workflow</Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.8)' }}>From {senderName}</Typography>
          </Box>
          <IconButton onClick={onClose} sx={{ color: '#fff' }}><Close /></IconButton>
        </Stack>
        {/* Stepper */}
        <Stack direction="row" alignItems="center" sx={{ mt: 2 }}>
          {STEPS.map((s, i) => (
            <React.Fragment key={s.key}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                onClick={() => i < step && setStep(i)}
                sx={{ cursor: i < step ? 'pointer' : 'default', opacity: i <= step ? 1 : 0.55 }}
              >
                <Box
                  sx={{
                    width: 30, height: 30, borderRadius: '50%', display: 'grid', placeItems: 'center', fontSize: 14,
                    bgcolor: i === step ? '#fff' : i < step ? wa.green : 'rgba(255,255,255,0.15)',
                    color: i === step ? wa.dark : '#fff', fontWeight: 800,
                    boxShadow: i === step ? '0 0 0 4px rgba(255,255,255,0.18)' : 'none',
                  }}
                >
                  {i < step ? <CheckCircle sx={{ fontSize: 18 }} /> : s.icon}
                </Box>
                <Typography fontWeight={700} fontSize={13} sx={{ color: '#fff', display: { xs: 'none', sm: 'block' } }}>{s.label}</Typography>
              </Stack>
              {i < STEPS.length - 1 && (
                <Box sx={{ flex: 1, height: 2, mx: 1.2, bgcolor: i < step ? wa.green : 'rgba(255,255,255,0.2)', borderRadius: 1 }} />
              )}
            </React.Fragment>
          ))}
        </Stack>
      </Box>

      <DialogContent sx={{ bgcolor: '#fafafa', p: { xs: 2, md: 3 } }}>{body}</DialogContent>

      <AudienceReviewDialog
        open={browseOpen}
        onClose={() => setBrowseOpen(false)}
        onApply={(excludedIds) => {
          const out = new Set(excludedIds);
          setMembers(
            (browseAll ?? [])
              .filter((m: any) => !out.has(m.id))
              .map((m: any) => ({ _id: m.id, name: m.name, phoneNumber: m.phone })),
          );
          setBrowseOpen(false);
        }}
        members={browseAll ? browseAll.map((m: any) => ({ id: m.id, name: m.name, subtitle: m.phone })) : null}
        initiallyExcluded={(browseAll ?? []).map((m: any) => m.id).filter((id: string) => !members.some((x) => x._id === id))}
        accent={wa.teal}
        title={isPlatform ? 'Pick SweatSocial app users' : 'Pick members'}
        applyLabel={(n) => `Select ${n} ${n === 1 ? 'member' : 'members'}`}
      />

      <AudienceReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        onApply={(ids) => { setExcluded(ids); setReviewOpen(false); }}
        members={reviewMembers}
        initiallyExcluded={excluded}
        accent={wa.teal}
        title={kind === 'automation' ? 'Who matches today' : 'Who gets this message?'}
      />

      {/* Footer */}
      <Box sx={{ px: 3, py: 1.8, borderTop: '1px solid rgba(0,0,0,0.07)', display: 'flex', alignItems: 'center', gap: 1, bgcolor: '#fff' }}>
        <Button onClick={() => (step === 0 ? onClose() : setStep(step - 1))} sx={{ fontWeight: 700 }}>
          {step === 0 ? 'Cancel' : 'Back'}
        </Button>
        <Box flexGrow={1} />
        {step < 3 ? (
          <Button
            variant="contained"
            disabled={!canNext}
            onClick={() => setStep(step + 1)}
            sx={{ background: wa.cta, boxShadow: wa.ctaShadow, borderRadius: 2, fontWeight: 800, px: 3 }}
          >
            Next
          </Button>
        ) : (
          <Button
            variant="contained"
            disabled={
              submitting ||
              scheduleInvalid ||
              (kind === 'automation' && !timeOk) ||
              (kind === 'campaign' && sendMode === 'now' && !recipients)
            }
            onClick={submit}
            startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : kind === 'automation' ? <Bolt /> : sendMode === 'now' ? <Send /> : <Schedule />}
            sx={{ background: wa.cta, boxShadow: wa.ctaShadow, borderRadius: 2, fontWeight: 800, px: 3 }}
          >
            {kind === 'automation'
              ? 'Start automation'
              : sendMode === 'now'
                ? `Send to ${recipients} ${recipients === 1 ? 'member' : 'members'}`
                : 'Schedule campaign'}
          </Button>
        )}
      </Box>
    </Dialog>
  );
};

export default WorkflowBuilder;
